import { Box, Flex, Icon, Text, useToast } from '@chakra-ui/react';
import {
  PAYMENT_PROOF_DROPZONE_ACCEPT,
  PAYMENT_PROOF_HELPER_TEXT,
  PAYMENT_PROOF_UPLOAD_CONSTRAINTS,
  paymentProofRejectMessage,
} from '@diamond/shared/schema/order';
import { showToast } from '@diamond/shared/ui';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { useCallback, useEffect } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import { validatePaymentProofFiles } from './payment-proof-file-validation';

interface DropzoneOcrProps {
  disabled?: boolean;
  maxFiles: number;
  maxW?: string | { base: string; md: string };
  onReady?: (open: (() => void) | null) => void;
  onFilesSelected: (files: File[]) => void;
}

function getRejectionMessage(rejections: FileRejection[], maxFiles: number) {
  const code = rejections[0]?.errors[0]?.code;
  if (code === 'file-too-large') {
    return paymentProofRejectMessage('file_too_large');
  }
  if (code === 'file-too-small') {
    return paymentProofRejectMessage('file_too_small');
  }
  if (code === 'file-invalid-type') {
    return paymentProofRejectMessage('invalid_type');
  }
  if (code === 'too-many-files') {
    return `Maksimal ${maxFiles} file yang dapat diunggah`;
  }
  return rejections[0]?.errors[0]?.message ?? 'File tidak dapat diunggah';
}

export const DropzoneOcr = ({
  disabled = false,
  maxFiles,
  maxW = { base: '100%', md: '330px' },
  onReady,
  onFilesSelected,
}: DropzoneOcrProps) => {
  const toast = useToast();

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      showToast(toast, 'error', getRejectionMessage(rejections, maxFiles));
    },
    [maxFiles, toast]
  );

  const handleDropAccepted = useCallback(
    async (files: File[]) => {
      const validation = await validatePaymentProofFiles(files);
      if (validation.isValid === false) {
        showToast(toast, 'error', validation.message);
        return;
      }
      onFilesSelected(files);
    },
    [onFilesSelected, toast]
  );

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: PAYMENT_PROOF_DROPZONE_ACCEPT,
    disabled,
    maxFiles,
    maxSize: PAYMENT_PROOF_UPLOAD_CONSTRAINTS.maxBytes,
    minSize: PAYMENT_PROOF_UPLOAD_CONSTRAINTS.minBytes,
    multiple: true,
    noClick: true,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  useEffect(() => {
    onReady?.(open);

    return () => {
      onReady?.(null);
    };
  }, [onReady, open]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    open();
  }, [disabled, open]);

  return (
    <Box>
      <Flex
        {...getRootProps()}
        align="center"
        bg={disabled ? '#F5F5F5' : '#F3F4F5'}
        border="2px dashed"
        borderColor={isDragActive ? '#0000A3' : '#D9D9D9'}
        borderRadius="8px"
        color={disabled ? '#A0A0A0' : '#131415'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        gap="12px"
        h="90px"
        justify="center"
        w="100%"
        maxW={maxW}
        px={6}
        py={6}
        transition="border-color 0.2s ease, background 0.2s ease"
        data-testid="dropzone-ocr"
        onClick={handleOpen}
      >
        <input {...getInputProps()} data-testid="dropzone-ocr-input" />
        <Icon as={CloudUploadOutlined} boxSize="32px" color="blue" />
        <Text fontSize="14px" lineHeight="20px">
          Tarik & lepas dokumen disini atau{' '}
          <Box as="span" color="blue" fontWeight={600}>
            Unggah
          </Box>
        </Text>
      </Flex>
      <Text
        color="#999999"
        fontSize="12px"
        lineHeight="15px"
        mt={2}
        w="100%"
        maxW={maxW}
      >
        {PAYMENT_PROOF_HELPER_TEXT}
      </Text>
    </Box>
  );
};
